import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { CheckCircle, Close } from '@mui/icons-material';
import api from '../../api';
import { COLORS } from '../../theme';
import { AVATARS } from '../../utils/avatar';

// Modal compartilhado para escolher o avatar do cliente.
// Mantém a chamada à API aqui para que qualquer gatilho (menu lateral, mobile
// etc.) reuse a mesma lógica e o mesmo feedback de erro/carregamento.
const AvatarPickerDialog = ({ open, onClose, currentAvatar, onSaved }) => {
  const theme = useTheme();
  const [savingAvatar, setSavingAvatar] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSavingAvatar(null);
      setError('');
    }
  }, [open]);

  const handleAvatarSelect = async (avatar) => {
    if (savingAvatar) return;

    if (avatar === currentAvatar) {
      onClose?.();
      return;
    }

    setSavingAvatar(avatar);
    setError('');

    try {
      await api.put('/user/avatar', { avatar });
      onSaved?.(avatar);
      onClose?.();
    } catch (requestError) {
      console.error('Erro ao atualizar avatar:', requestError);
      setError('Não foi possível atualizar o avatar. Tente novamente.');
    } finally {
      setSavingAvatar(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={savingAvatar ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          fontWeight: 800,
          pb: 1,
        }}
      >
        Escolha seu avatar
        <IconButton
          onClick={onClose}
          disabled={Boolean(savingAvatar)}
          aria-label="Fechar seleção de avatar"
          size="small"
          sx={{ color: theme.palette.text.secondary }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Selecione uma das opções abaixo para personalizar seu perfil.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '14px' }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 1.5,
            pb: 1,
          }}
        >
          {AVATARS.map((avatar, index) => {
            const selected = avatar === currentAvatar;
            const saving = avatar === savingAvatar;

            return (
              <Box
                key={avatar}
                component="button"
                type="button"
                onClick={() => handleAvatarSelect(avatar)}
                disabled={Boolean(savingAvatar)}
                aria-label={`Selecionar avatar ${index + 1}`}
                aria-pressed={selected}
                sx={{
                  position: 'relative',
                  border: 0,
                  p: 0,
                  bgcolor: 'transparent',
                  cursor: savingAvatar ? 'wait' : 'pointer',
                  borderRadius: '50%',
                  lineHeight: 0,
                  justifySelf: 'center',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: savingAvatar ? 'none' : 'translateY(-2px)' },
                  '&:focus-visible': {
                    outline: `2px solid ${COLORS.orange}`,
                    outlineOffset: 3,
                  },
                }}
              >
                <Avatar
                  src={avatar}
                  alt={`Avatar ${index + 1}`}
                  sx={{
                    width: 64,
                    height: 64,
                    border: selected ? `3px solid ${COLORS.orange}` : '3px solid transparent',
                    opacity: savingAvatar && !saving ? 0.5 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                />

                {saving && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(COLORS.dark, 0.45),
                      borderRadius: '50%',
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: COLORS.white }} />
                  </Box>
                )}

                {selected && !saving && (
                  <CheckCircle
                    sx={{
                      position: 'absolute',
                      right: -2,
                      bottom: -2,
                      fontSize: 22,
                      color: COLORS.orange,
                      bgcolor: theme.palette.background.paper,
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarPickerDialog;
